/**
 * Safe Spot design tokens — lifted from the Safe Spot spec's safe-spot.css.
 *
 * The colour rule is the whole point and it is semantic, not decorative:
 *
 *   PURPLE means a PERSON. Collector pins, avatars, the chat's own chrome.
 *   SAGE   means a PLACE.   Venue markers, venue tiles, the meetup card.
 *   TERRACOTTA means the primary action on a place.
 *
 * A venue drawn in purple would read as a collector on the map, and a meetup
 * card in the chat's purple would read as another message from the other person
 * rather than as a place. Keeping the two families apart is what lets someone
 * glance at a screen and know whether they are looking at a who or a where.
 *
 * Exported as plain values rather than CSS custom properties because the app
 * styles through MUI's `sx`, and a JS object is what that consumes. The names
 * mirror the --ss-* properties in the spec so the two stay diffable.
 */
export const SS = {
  venue: "#728157", // sage 600 — venue identity fill
  venueDeep: "#3d472b", // sage 800 — text on sage tint
  venueStrong: "#56633f", // sage 700 — "Open now" on paper
  venueTint: "#e1eecc", // sage 200 — trust badge, halo
  action: "#c67139", // terracotta 500 — primary CTA
  actionHover: "#b2622d",
  actionPress: "#8c491a",
  closedInk: "#8c491a", // deep terracotta — "Closed"
  paper: "#fffdf7",
  ink: "#201e1d",
  inkSoft: "#6d675e",
  muted: "#82796a",
  line: "#dcd3c4",
  wash: "#f9f4ed",
};

/**
 * The one trust claim we can defend.
 *
 * Not "Verified safe": that promises an inspection nobody performed, and it is
 * the phrase that would be quoted back at us the first time something went
 * wrong at a venue. This states what actually happened — the venue opted in.
 */
export const TRUST_LABEL = "Partner store — agreed to host trades";

/**
 * Marker/tile glyph per category, as SVG path sets.
 *
 * Inline paths rather than an icon font or an icon package: these render inside
 * Mapbox markers and MUI boxes alike, and the spec ships them as inline SVG for
 * exactly that reason. An unknown category falls back to the shop glyph so a
 * new category is never a blank tile.
 */
export const CATEGORY_GLYPHS = {
  shop: [
    "m2 7 4.41-4.41A2 2 0 0 1 7.83 2h8.34a2 2 0 0 1 1.42.59L22 7",
    "M4 12v8a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-8",
    "M15 22v-4a2 2 0 0 0-2-2h-2a2 2 0 0 0-2 2v4",
    "M2 7h20",
  ],
  mall: ["M6 2 3 6v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2V6l-3-4z", "M3 6h18", "M16 10a4 4 0 0 1-8 0"],
  community_centre: [
    "M3 22h18",
    "M6 18v-7",
    "M10 18v-7",
    "M14 18v-7",
    "M18 18v-7",
    "m12 2 8 5H4z",
  ],
  library: ["M4 19.5A2.5 2.5 0 0 1 6.5 17H20", "M6.5 2H20v20H6.5A2.5 2.5 0 0 1 4 19.5v-15A2.5 2.5 0 0 1 6.5 2z"],
  cafe: ["M10 2v2", "M14 2v2", "M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1z", "M6 2v2"],
  other: [
    "M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0",
    "M12 13a3 3 0 1 0 0-6 3 3 0 0 0 0 6",
  ],
};

export const glyphFor = (category) =>
  CATEGORY_GLYPHS[category] || CATEGORY_GLYPHS.shop;

/**
 * Which of a venue's two names leads, and which is the co-title.
 *
 * Both carry full weight in the spec — the second language is a co-title, not a
 * caption — so this only decides order. Falls back to whichever name exists so a
 * venue with one name never renders blank.
 */
export const venueNames = (spot, lang = "en") => {
  // A stored document and toPublic() both use {en, he}. The earlier map
  // fixtures used a single string. Reading both means a missed migration still
  // renders a name rather than "[object Object]".
  if (typeof spot?.name === "string") {
    return { primary: spot.name, secondary: spot.nameHe || "" };
  }
  const en = spot?.name?.en || "";
  const he = spot?.name?.he || "";
  const primary = lang === "he" ? he || en : en || he;
  const secondary = lang === "he" ? (he ? en : "") : en ? he : "";
  return { primary, secondary };
};
